// src/components/roomBookingCheck.js
//
// The "is this room actually free?" confirmation shared by every place RegX
// books onto the Research calendar — the calendar page's CalendarEventModal and
// PatientDetailPage's ScheduleVisitModal.
//
// WHY IT EXISTS: the Research Google Calendar is the source of truth and plenty
// of visits are booked straight from Google, where there's no room field. Those
// arrive in RegX with no room recorded, so RegX can never honestly say a room is
// free — the most it can do is show what else is in that slot and make someone
// look before they book. Nothing here ever blocks a booking.
//
// It deliberately re-reads the chosen DAY from the calendar when the dialog
// opens instead of trusting whatever a caller already had loaded: that makes the
// check work for any date (not just a week already on screen) and catches
// anything booked while the form sat open. A failed read says so out loud rather
// than showing an empty list, which would read as a false all-clear.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, CalendarPlus, Loader2 } from "lucide-react";
import { listCalendarEvents } from "../services/researchCalendarService";

// Stable identity so the memo below doesn't re-run on every parent render.
const NO_EVENTS = [];

const fmtClock = (d) =>
  d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
// Exported so callers can label events the same way the dialog does.
export const fmtRange = (ev) =>
  `${fmtClock(ev.startDate)}–${fmtClock(ev.endDate)}`;
// "14:30" (an <input type=time> value) → "2:30 PM", matching the event lists.
const fmtInputTime = (hhmm) => {
  const d = new Date(`2000-01-01T${hhmm}`);
  return isNaN(d) ? hhmm : fmtClock(d);
};
const fmtInputDate = (ymd) => {
  const d = new Date(`${ymd}T00:00`);
  return isNaN(d)
    ? ymd
    : d.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
};

// How an event's room is compared. "Other" carries its real name in otherRoom;
// an empty result means the event names no room at all, which is the Google
// Calendar case.
const roomKey = (name, other) =>
  String(name === "Other" ? other || "" : name || "")
    .trim()
    .toLowerCase();

const eventRoomLabel = (ev) => {
  if (!ev.room) return "no room";
  return ev.room === "Other" && ev.otherRoom ? ev.otherRoom : ev.room;
};

/**
 * Drives the room-check confirmation for one booking form.
 *
 * @param open          whether the parent form is open (resets on close)
 * @param resetKey      changes when the form switches to a different event
 * @param date/startTime/endTime/room/otherRoom  the slot being booked
 * @param excludeEventId  the event being edited — never clashes with itself
 * @param seedEvents    already-loaded events to fall back on if the read fails
 */
export function useRoomBookingCheck({
  open,
  resetKey,
  date,
  startTime,
  endTime,
  room,
  otherRoom = "",
  excludeEventId = "",
  seedEvents = NO_EVENTS,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // { forDate, events } — `forDate` is what makes a result safe to reuse, and
  // stops a slow response for one date being applied to another.
  const [dayLookup, setDayLookup] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  useEffect(() => {
    setConfirmOpen(false);
    setDayLookup(null);
    setCheckFailed(false);
  }, [open, resetKey]);

  const checkedThisDay = !!dayLookup && dayLookup.forDate === date;
  const checkedEvents = checkedThisDay ? dayLookup.events : seedEvents;

  // What else sits in this slot, split by whether RegX can tell the room:
  //   conflicts    — same room. A real clash.
  //   unverifiable — overlapping with no room recorded (the Google Calendar ones).
  //   overlaps     — everything in the slot, whatever room it claims.
  const { conflicts, unverifiable, overlaps } = useMemo(() => {
    const empty = { conflicts: [], unverifiable: [], overlaps: [] };
    if (!open || !date || !startTime || !endTime || !room) return empty;
    const start = new Date(`${date}T${startTime}`);
    const end = new Date(`${date}T${endTime}`);
    if (isNaN(start) || isNaN(end) || end <= start) return empty;
    const mine = roomKey(room, otherRoom);
    const found = { conflicts: [], unverifiable: [], overlaps: [] };
    for (const ev of checkedEvents) {
      if (!ev?.startDate || !ev?.endDate) continue;
      if (excludeEventId && ev.id === excludeEventId) continue;
      if (!(ev.startDate < end && start < ev.endDate)) continue;
      found.overlaps.push(ev);
      const theirs = roomKey(ev.room, ev.otherRoom);
      if (!theirs) found.unverifiable.push(ev);
      else if (mine && theirs === mine) found.conflicts.push(ev);
    }
    return found;
  }, [
    open,
    date,
    startTime,
    endTime,
    room,
    otherRoom,
    checkedEvents,
    excludeEventId,
  ]);

  // Pull the whole of the chosen day from the calendar. Widened past midnight at
  // both ends so a visit starting the evening before, or running past midnight,
  // still turns up; the overlap test above does the precise work.
  const loadDay = useCallback(async (forDate) => {
    setChecking(true);
    setCheckFailed(false);
    try {
      const dayStart = new Date(`${forDate}T00:00`);
      if (isNaN(dayStart)) throw new Error("Bad date");
      const timeMin = new Date(dayStart.getTime() - 12 * 3600e3).toISOString();
      const timeMax = new Date(dayStart.getTime() + 36 * 3600e3).toISOString();
      const raw = await listCalendarEvents(timeMin, timeMax);
      const parsed = (raw || [])
        .filter((e) => e.start && e.end)
        .map((e) => ({
          ...e,
          startDate: new Date(e.start),
          endDate: new Date(e.end),
        }))
        .filter((e) => !isNaN(e.startDate) && !isNaN(e.endDate));
      setDayLookup({ forDate, events: parsed });
    } catch {
      // Never blocks the booking — fall back to whatever the caller had and say
      // so, so nobody reads silence as "the room is free".
      setCheckFailed(true);
    } finally {
      setChecking(false);
    }
  }, []);

  // Open the confirmation and (re)read the day behind it.
  const requestCheck = useCallback(() => {
    setConfirmOpen(true);
    if (!checkedThisDay) loadDay(date);
  }, [checkedThisDay, loadDay, date]);

  const dismiss = useCallback(() => setConfirmOpen(false), []);

  const roomIsUnnamed = room === "Other" && !String(otherRoom).trim();
  const roomBeingBooked =
    room === "Other" ? String(otherRoom).trim() || "this room" : room;

  return {
    confirmOpen,
    requestCheck,
    dismiss,
    conflicts,
    unverifiable,
    // Props for <RoomCheckDialog>. Spread them; the caller adds onConfirm/saving.
    dialogProps: {
      open: confirmOpen,
      onCancel: dismiss,
      onRetry: () => loadDay(date),
      checking,
      checkFailed,
      conflicts,
      unverifiable,
      overlaps,
      roomIsUnnamed,
      roomBeingBooked,
      date,
      startTime,
      endTime,
    },
  };
}

// Every booking is listed — no truncation. The body scrolls if it needs to;
// a hidden "and 3 more…" is exactly the one you'd have wanted to see.
const EventList = ({ label, events, className = "", withRoom = false }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </p>
    <ul className={`space-y-1 rounded-lg px-3 py-2 ${className}`}>
      {events.map((ev) => (
        <li key={ev.id}>
          {fmtRange(ev)}
          {withRoom && (
            <span className="text-slate-400"> · {eventRoomLabel(ev)}</span>
          )}
          <span className="text-slate-400"> · </span>
          {ev.patientName || "Visit"}
          {ev.coordinatorName ? (
            <span className="text-slate-400"> · {ev.coordinatorName}</span>
          ) : null}
        </li>
      ))}
    </ul>
  </div>
);

/**
 * The confirmation itself. Its own overlay above the form (z-[130]) so it has to
 * be answered before anything is written. Both buttons are always available —
 * this asks someone to look, it never refuses a booking.
 */
export function RoomCheckDialog({
  open,
  onCancel,
  onConfirm,
  onRetry,
  saving = false,
  checking,
  checkFailed,
  conflicts,
  unverifiable,
  overlaps,
  roomIsUnnamed,
  roomBeingBooked,
  date,
  startTime,
  endTime,
  confirmLabel = "I've checked — book it",
}) {
  // Don't colour the dialog red off stale seed data while the real day is still
  // loading — the list it would be drawn from is about to be replaced.
  const showsConflict = !checking && conflicts.length > 0;
  const slot = `${fmtInputDate(date)}, ${fmtInputTime(startTime)}–${fmtInputTime(endTime)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !saving && onCancel?.()}
        >
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="room-check-title"
            className={`w-full max-w-md rounded-2xl border bg-[#0e1016]/95 shadow-2xl overflow-hidden ${
              showsConflict ? "border-rose-400/30" : "border-amber-400/30"
            }`}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center gap-2 px-5 py-4 border-b ${
                showsConflict
                  ? "border-rose-400/20 bg-rose-500/10"
                  : "border-amber-400/20 bg-amber-500/10"
              }`}
            >
              <AlertTriangle
                className={`w-4 h-4 shrink-0 ${
                  showsConflict ? "text-rose-300" : "text-amber-300"
                }`}
              />
              <h3 id="room-check-title" className="text-sm font-semibold text-white">
                {checking
                  ? `Checking ${roomBeingBooked}…`
                  : showsConflict
                    ? `${roomBeingBooked} looks already booked`
                    : `Double-check ${roomBeingBooked} is free`}
              </h3>
            </div>

            <div className="px-5 py-4 space-y-3 text-[12px] text-slate-300 max-h-[60vh] overflow-y-auto">
              {/* The slot, stated once, so nothing below has to repeat it. */}
              <p className="text-slate-100">{slot}</p>

              {checking && (
                <p className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  Checking the calendar…
                </p>
              )}

              {checkFailed && (
                <p className="text-slate-400">
                  Couldn't reach the calendar — this list may be incomplete.
                </p>
              )}

              {!checking && (
                <>
                  {showsConflict && (
                    <EventList
                      label={`Already in ${roomBeingBooked}`}
                      events={conflicts}
                      className="border border-rose-400/20 bg-rose-500/5 text-rose-100"
                    />
                  )}

                  {roomIsUnnamed ? (
                    overlaps.length > 0 ? (
                      <EventList
                        label="Booked at this time"
                        events={overlaps}
                        withRoom
                        className="border border-white/10 bg-white/[0.03] text-slate-200"
                      />
                    ) : (
                      <p className="text-slate-400">Nothing else booked then.</p>
                    )
                  ) : unverifiable.length > 0 ? (
                    <EventList
                      label="At this time, no room recorded"
                      events={unverifiable}
                      className="border border-white/10 bg-white/[0.03] text-slate-200"
                    />
                  ) : (
                    !showsConflict && (
                      <p className="text-slate-400">
                        Nothing booked in {roomBeingBooked}.
                      </p>
                    )
                  )}
                </>
              )}

              <p className="text-amber-200">
                Visits booked in Google Calendar don't record a room — check it
                before booking.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 transition disabled:opacity-50"
              >
                Go back
              </button>
              {checkFailed && (
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={saving || checking}
                  className="px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 border border-white/10 transition disabled:opacity-50"
                >
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={onConfirm}
                disabled={saving || checking}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_4px_12px_-2px_rgba(245,158,11,0.5)] hover:brightness-110 transition disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CalendarPlus className="w-3.5 h-3.5" />
                )}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Shared by the callers for their own "did the bookable part change?" test.
export const slotChanged = (fields, previous = {}) =>
  fields.date !== (previous.date || "") ||
  fields.startTime !== (previous.startTime || "") ||
  fields.endTime !== (previous.endTime || "") ||
  fields.room !== (previous.room || "") ||
  fields.otherRoom !== (previous.otherRoom || "");
